import React, { useEffect, useState } from 'react';
import ActionBadge from './ActionBadge';
import ActionType from './ActionType';
import { getAllOrderTemplates } from '../../../../services/OrderTemplatesService';
import { getWithdrawTemplates } from '../../../../services/WithdrawTemplatesService';
import { getSymbol } from '../../../../services/SymbolsService';

function filterTemplatesForSide(templates, orderSide) {
    if (!orderSide) return templates;
    const normalized = String(orderSide).toUpperCase();
    return (templates || []).filter(
        (template) => !template.side || template.side.toUpperCase() === normalized,
    );
}

/**
 * props:
 * - symbol
 * - actions
 * - onChange
 * - orderSide: optional 'BUY' | 'SELL' filter for order template picks
 * - variant: 'inline' | 'stacked'
 */
function ActionsArea(props) {
    const variant = props.variant || 'inline';

    const DEFAULT_ACTION = {
        type: 'ALERT_EMAIL',
        orderTemplateId: null,
        orderTemplateName: '',
        withdrawTemplateId: null,
        withdrawTemplateName: ''
    }

    const [newAction, setNewAction] = useState(DEFAULT_ACTION);
    const [orderTemplates, setOrderTemplates] = useState([]);
    const [withdrawTemplates, setWithdrawTemplates] = useState([]);
    const [actions, setActions] = useState([]);

    const templatesForSide = filterTemplatesForSide(orderTemplates, props.orderSide);

    function onInputChange(event) {
        if (event.target.id === 'orderTemplateId') {
            const orderTemplateId = parseInt(event.target.value);
            const orderTemplate = templatesForSide.find(ot => ot.id === orderTemplateId);
            setNewAction(prevState => ({
                ...prevState,
                orderTemplateName: orderTemplate.name,
                orderTemplateId
            }));
        }
        else if (event.target.id === 'withdrawTemplateId') {
            const withdrawTemplateId = parseInt(event.target.value);
            const withdrawTemplate = withdrawTemplates.find(ot => ot.id === withdrawTemplateId);
            setNewAction(prevState => ({
                ...prevState,
                withdrawTemplateName: withdrawTemplate.name,
                withdrawTemplateId
            }));
        }
        else
            setNewAction(prevState => ({ ...prevState, [event.target.id]: event.target.value }));

        if (props.onChange) props.onChange(event);
    }

    useEffect(() => {

        if (props.actions) {
            const actions = props.actions.sort((a, b) => {
                if (a.id > b.id) return 1;
                if (a.id < b.id) return -1;
                return 0;
            })

            setActions(actions);
        } else setActions([]);

        setNewAction(DEFAULT_ACTION);
    }, [props.actions])

    useEffect(() => {
        if (!props.symbol) return;

        getAllOrderTemplates(props.symbol)
            .then(result => setOrderTemplates(result))
            .catch(err => console.error(err.response ? err.response.data : err.message));

        getSymbol(props.symbol)
            .then(symbol => {
                if (symbol)
                    return getWithdrawTemplates(symbol.base, 1);
                else
                    throw new Error(`No withdraw templates found.`);
            })
            .then(result => setWithdrawTemplates(result.rows))
            .catch(err => console.error(err.response ? err.response.data : err.message));

    }, [props.symbol])

    function onAddClick(event) {
        const actionToAdd = { ...newAction };

        if (['ORDER', 'TRAILING'].includes(actionToAdd.type)) {
            if (!actionToAdd.orderTemplateId) return;
            actionToAdd.id = 'ot' + actionToAdd.orderTemplateId;

            const alreadyExists = actions.some(a => a.id === actionToAdd.id);
            if (alreadyExists) return;
        }
        else if (actionToAdd.type === 'WITHDRAW') {
            if (!actionToAdd.withdrawTemplateId) return;
            actionToAdd.id = 'wt' + actionToAdd.withdrawTemplateId;

            const alreadyExists = actions.some(a => a.id === actionToAdd.id);
            if (alreadyExists) return;
        }
        else {
            actionToAdd.id = actionToAdd.type;
            const alreadyExists = actions.some(a => a.type === actionToAdd.type);
            if (alreadyExists) return;
        }

        const nextActions = [...actions, actionToAdd];
        setActions(nextActions);
        if (props.onChange) props.onChange({ target: { id: 'actions', value: nextActions } });
        setNewAction(DEFAULT_ACTION);
    }

    function onRemoveActionClick(event) {
        const index = actions.findIndex(a => a.id == event.target.id);
        if (index < 0) return;
        const nextActions = actions.filter((_, i) => i !== index);
        setActions(nextActions);
        if (props.onChange) props.onChange({ target: { id: 'actions', value: nextActions } });
    }

    const orderTemplateSelect = newAction.type === 'ORDER' && templatesForSide.length ? (
        <select id="orderTemplateId" className="form-select" onChange={onInputChange}>
            <option value="0">Select one...</option>
            {
                templatesForSide
                    .filter(ot => ot.type !== 'TRAILING_STOP')
                    .map(ot => (<option key={ot.id} value={ot.id}>{ot.name}</option>))
            }
        </select>
    ) : null;

    const trailingTemplateSelect = newAction.type === 'TRAILING' && templatesForSide.length ? (
        <select id="orderTemplateId" className="form-select" onChange={onInputChange}>
            <option value="0">Select one...</option>
            {
                templatesForSide
                    .filter(ot => ot.type === 'TRAILING_STOP')
                    .map(ot => (<option key={ot.id} value={ot.id}>{ot.name}</option>))
            }
        </select>
    ) : null;

    const withdrawTemplateSelect = newAction.type === 'WITHDRAW' && withdrawTemplates ? (
        <select id="withdrawTemplateId" className="form-select" onChange={onInputChange}>
            <option value="0">Select one...</option>
            {
                withdrawTemplates.map(wt => (<option key={wt.id} value={wt.id}>{wt.name}</option>))
            }
        </select>
    ) : null;

    const templateSelect = orderTemplateSelect || trailingTemplateSelect || withdrawTemplateSelect;

    const addButton = (
        <button
            type="button"
            className="btn btn-primary btn-sm actions-add-btn"
            onClick={onAddClick}
            title="Add action"
        >
            <svg className="icon icon-xs" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
            </svg>
        </button>
    );

    const actionInputs = variant === 'stacked' ? (
        <div className="actions-area--stacked">
            <div className="private-page-field">
                <label htmlFor="type" className="private-page-label">Action type</label>
                {templateSelect ? (
                    <ActionType type={newAction.type} onChange={onInputChange} />
                ) : (
                    <div className="actions-value-row">
                        <ActionType type={newAction.type} onChange={onInputChange} />
                        {addButton}
                    </div>
                )}
            </div>
            {templateSelect ? (
                <div className="private-page-field">
                    <label
                        htmlFor={newAction.type === 'WITHDRAW' ? 'withdrawTemplateId' : 'orderTemplateId'}
                        className="private-page-label"
                    >
                        Template
                    </label>
                    <div className="actions-value-row">
                        {templateSelect}
                        {addButton}
                    </div>
                </div>
            ) : null}
        </div>
    ) : (
        <div className="input-group input-group-merge">
            <ActionType type={newAction.type} onChange={onInputChange} />
            {templateSelect}
            {addButton}
        </div>
    );

    return (
        <React.Fragment>
            {variant === 'stacked' ? (
                actionInputs
            ) : (
                <div className="row">
                    <div className="col-12 my-3">
                        {actionInputs}
                    </div>
                </div>
            )}
            {
                actions && actions.length > 0
                    ? (
                        <div className="divScrollBadges">
                            <div className="d-inline-flex flex-row flex-wrap align-content-start">
                                {
                                    actions.map(action => (<ActionBadge key={action.type + ":" + action.id} action={action} onClick={onRemoveActionClick} />))
                                }
                            </div>
                        </div>
                    )
                    : <React.Fragment></React.Fragment>
            }
        </React.Fragment >
    )
}

export default ActionsArea;
